import messages from "messages";
import { useEffect, useRef, useState, MouseEventHandler } from "react";
import { Button } from "react-bootstrap";
import getClasses from "utils/getClasses";
import styles from "./styles.module.scss";
import DOMPurify from "dompurify";

type Props = {
  content: string;
  /**
   * by default, the content passed will be sanitzed to prevent XSS attack
   * if set to true, will not sanitize the content
   */
  noSanitize?: boolean;
  lines?: number;
  expanded?: boolean;
  /**
   * additional class for root container
   */
  rootClass?: string;
  /**
   * additional class for text container
   */
  textContainerClass?: string;
  /**
   * additional class for button container
   */
  buttonContainerClass?: string;
  onClick?: MouseEventHandler;
};

const ShowMore = ({
  content,
  lines = 5,
  expanded = false,
  noSanitize,
  rootClass,
  textContainerClass,
  buttonContainerClass,
  onClick,
}: Props) => {
  const [open, setOpen] = useState(expanded);
  const textContainerRef = useRef<HTMLDivElement | null>(null);

  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    setOverflow(false);
    const { current: textContainer } = textContainerRef;
    if (!textContainer) {
      return;
    }
    const maxHeight = +getComputedStyle(textContainer).lineHeight.replace(
      /[a-z]*$/,
      "",
    );

    if ((textContainer?.scrollHeight || 0) > (maxHeight * lines || 0)) {
      setOverflow(true);
    }
    return () => {
      // reset when content changed
      setOverflow(false);
    };
  }, [content, lines]);

  const full = open || !overflow;

  return (
    <div className={getClasses(styles["show-more"], rootClass)}>
      <div
        className={getClasses(
          styles["show-more__text-container"],
          !full && styles["show-more__text-container--clamped"],
          textContainerClass,
        )}
        style={{
          height: full ? "auto" : `${lines * 1.5}em`,
        }}
        dangerouslySetInnerHTML={{
          __html: !noSanitize ? DOMPurify.sanitize(content) : content,
        }}
        ref={textContainerRef}
      />
      {overflow && (
        <Button
          variant="link"
          className={getClasses(
            styles["show-more__button-container"],
            buttonContainerClass,
          )}
          onClick={(e) => {
            setOpen(!open);
            return onClick && onClick(e);
          }}
        >
          {`${messages.read} ${open ? messages.less : messages.more}`}
        </Button>
      )}
    </div>
  );
};

export default ShowMore;
